/**
 * SettingsFields
 *
 * The shared building blocks for the Settings dialog — the read-only rows, the
 * edit-in-place rows, the dropdowns and the tinted notice panels. Kept in one
 * place so every section looks and behaves the same, and so a fix to (say) how
 * a save error is shown lands everywhere at once.
 *
 * The editing pattern throughout is deliberately one row at a time: click
 * Edit, change the one thing, Save. No form-wide "unsaved changes" state to
 * lose, and a failed save can only ever affect the field you were editing.
 */

import { useEffect, useRef, useState } from 'react'
import { JAI_COLORS, JAI_FONTS } from '../JaiBrand'

// Re-exported so the settings sections keep importing dates from one place.
// The real implementation lives in utils/datetime.js, which every date in the
// app now goes through so the user's timezone preference actually applies.
export { formatDate } from '../../utils/datetime'

const LABEL_WIDTH = 116

const rowStyle = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 16,
  padding: '13px 0',
  borderBottom: `1px solid ${JAI_COLORS.hair}`,
}

const labelStyle = {
  width: LABEL_WIDTH,
  flexShrink: 0,
  fontSize: 12.5,
  fontWeight: 600,
  color: JAI_COLORS.mute,
}

const valueStyle = {
  flex: 1,
  fontSize: 14.5,
  fontWeight: 500,
  color: JAI_COLORS.navy,
  wordBreak: 'break-word',
  fontFamily: JAI_FONTS.tight,
}

export const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 9,
  border: `1px solid ${JAI_COLORS.hair}`,
  fontFamily: JAI_FONTS.tight,
  fontSize: 14.5,
  color: JAI_COLORS.navy,
  background: 'white',
  outline: 'none',
  boxSizing: 'border-box',
}

// ── Read-only row ────────────────────────────────────────────────────────────

export function Row({ label, value, mono = false }) {
  return (
    <div style={rowStyle}>
      <div style={labelStyle}>{label}</div>
      <div style={{ ...valueStyle, fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : JAI_FONTS.tight }}>
        {value || '—'}
      </div>
    </div>
  )
}

export function SectionNote({ children }) {
  return (
    <p style={{ margin: '16px 0 0 0', fontSize: 12.5, lineHeight: 1.6, color: JAI_COLORS.mute }}>
      {children}
    </p>
  )
}

/** A note with a thin rule on the left (no tinted panel). Carries disclosures, warnings and confirmations. */
export function Notice({ tone = 'neutral', title, children }) {
  const rule = {
    neutral: JAI_COLORS.hair,
    warn:    '#9A6A00',
    good:    '#1A8754',
    danger:  '#D63A3A',
  }[tone]

  return (
    <div style={{ marginTop: 16, padding: '2px 0 2px 14px', borderLeft: `3px solid ${rule}`, borderRadius: 0 }}>
      {title && (
        <div style={{ fontSize: 13.5, fontWeight: 650, color: JAI_COLORS.navy, marginBottom: 6 }}>
          {title}
        </div>
      )}
      <div style={{ fontSize: 13, lineHeight: 1.65, color: JAI_COLORS.ink }}>
        {children}
      </div>
    </div>
  )
}

/** Inline error text under a field. */
export function FieldError({ children }) {
  if (!children) return null
  return (
    <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: JAI_COLORS.error }}>
      {children}
    </div>
  )
}

/** Brief "Saved" flash, so a save that changes nothing visible still confirms. */
export function SavedFlash({ show }) {
  if (!show) return null
  return (
    <span style={{ marginLeft: 10, fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.success }}>
      Saved
    </span>
  )
}

// ── Small buttons used inside rows ───────────────────────────────────────────

export function LinkButton({ children, onClick, disabled, tone = 'blue' }) {
  const color = tone === 'danger' ? JAI_COLORS.error : JAI_COLORS.blue
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        border: 'none', background: 'transparent', padding: 0,
        fontFamily: JAI_FONTS.tight, fontSize: 12.5, fontWeight: 600,
        color, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  )
}

// ── Edit-in-place row ────────────────────────────────────────────────────────

/**
 * A row that swaps into an input when you click Edit.
 *
 * onSave receives the new value and should throw (or reject) with a message to
 * show inline — the row stays open on failure so the typed value isn't lost.
 *
 * `checkAvailability` is optional and drives the live "taken / available" hint
 * used by the username row.
 */
export function EditableRow({
  label,
  value,
  display,
  onSave,
  placeholder,
  prefix,
  maxLength,
  helper,
  checkAvailability,
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [availability, setAvailability] = useState(null) // null | 'checking' | 'free' | 'taken'
  const inputRef = useRef(null)

  const open = () => {
    setDraft(value || '')
    setError('')
    setAvailability(null)
    setEditing(true)
  }

  const close = () => {
    setEditing(false)
    setError('')
    setAvailability(null)
  }

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  // Live availability check, debounced so it doesn't fire on every keystroke.
  useEffect(() => {
    if (!editing || !checkAvailability) return
    const trimmed = draft.trim()
    if (!trimmed || trimmed === (value || '')) { setAvailability(null); return }

    setAvailability('checking')
    const timer = setTimeout(async () => {
      try {
        const free = await checkAvailability(trimmed)
        setAvailability(free ? 'free' : 'taken')
      } catch {
        setAvailability(null)   // couldn't check — let the save attempt decide
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [draft, editing, checkAvailability, value])

  const save = async () => {
    const trimmed = draft.trim()
    if (trimmed === (value || '')) { close(); return }

    setBusy(true)
    setError('')
    try {
      await onSave(trimmed)
      setEditing(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2200)
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save that. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save() }
    if (e.key === 'Escape') { e.preventDefault(); close() }
  }

  if (!editing) {
    return (
      <div style={rowStyle}>
        <div style={labelStyle}>{label}</div>
        <div style={valueStyle}>
          {display || value || '—'}
          <SavedFlash show={saved} />
        </div>
        <LinkButton onClick={open}>Edit</LinkButton>
      </div>
    )
  }

  const hint = {
    checking: { text: 'Checking…',  color: JAI_COLORS.mute },
    free:     { text: 'Available',  color: JAI_COLORS.success },
    taken:    { text: 'Taken',      color: JAI_COLORS.error },
  }[availability]

  return (
    <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
      <div style={{ ...labelStyle, paddingTop: 10 }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {prefix && (
            <span style={{ fontSize: 14.5, fontWeight: 600, color: JAI_COLORS.mute, flexShrink: 0 }}>
              {prefix}
            </span>
          )}
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-label={label}
            style={inputStyle}
          />
        </div>

        {hint && (
          <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 600, color: hint.color }}>
            {hint.text}
          </div>
        )}
        {helper && !hint && (
          <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: JAI_COLORS.mute }}>
            {helper}
          </div>
        )}
        <FieldError>{error}</FieldError>

        <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
          <LinkButton onClick={save} disabled={busy || availability === 'taken'}>
            {busy ? 'Saving…' : 'Save'}
          </LinkButton>
          <LinkButton onClick={close} disabled={busy} tone="danger">Cancel</LinkButton>
        </div>
      </div>
    </div>
  )
}

// ── Dropdown row ─────────────────────────────────────────────────────────────

/**
 * A labelled <select> that saves the moment it changes.
 *
 * Preferences are all low-stakes and instantly reversible, so making someone
 * press Save as well would be friction for nothing.
 */
export function SelectRow({ label, value, options, onSave, helper }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  const change = async (next) => {
    setBusy(true)
    setError('')
    try {
      await onSave(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 2200)
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save that. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
      <div style={{ ...labelStyle, paddingTop: 10 }}>
        {label}
        <SavedFlash show={saved} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <select
          value={value ?? ''}
          disabled={busy}
          onChange={e => change(e.target.value)}
          aria-label={label}
          style={{ ...inputStyle, cursor: busy ? 'wait' : 'pointer' }}
        >
          {options.map(opt => (
            <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {helper && (
          <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: JAI_COLORS.mute }}>
            {helper}
          </div>
        )}
        <FieldError>{error}</FieldError>
      </div>
    </div>
  )
}

// ── Choice cards (used for advice style) ─────────────────────────────────────

/**
 * A row of mutually exclusive cards. Used where the options need a sentence of
 * explanation each — "Bold" versus "Safe" means nothing without it, and these
 * genuinely change what JAI recommends rather than just how it's worded.
 */
export function ChoiceCards({ label, value, options, onSave, helper }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  const choose = async (next) => {
    if (next === value) return
    setBusy(true)
    setError('')
    try {
      await onSave(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 2200)
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save that. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '15px 0', borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.mute, marginBottom: 10 }}>
        {label}
        <SavedFlash show={saved} />
      </div>

      <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map(opt => {
          const on = opt.value === value
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={busy}
              onClick={() => choose(opt.value)}
              style={{
                flex: '1 1 150px',
                minWidth: 140,
                textAlign: 'left',
                padding: '11px 13px',
                borderRadius: 11,
                cursor: busy ? 'wait' : 'pointer',
                background: 'white',
                border: `1.5px solid ${on ? JAI_COLORS.blue : JAI_COLORS.hair}`,
                fontFamily: JAI_FONTS.tight,
                transition: 'border-color 150ms, background 150ms',
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 650, color: on ? JAI_COLORS.blue : JAI_COLORS.navy }}>
                {opt.label}
              </div>
              <div style={{ marginTop: 3, fontSize: 12, lineHeight: 1.5, color: JAI_COLORS.mute }}>
                {opt.detail}
              </div>
            </button>
          )
        })}
      </div>

      {helper && (
        <div style={{ marginTop: 9, fontSize: 12.5, lineHeight: 1.55, color: JAI_COLORS.mute }}>
          {helper}
        </div>
      )}
      <FieldError>{error}</FieldError>
    </div>
  )
}

// ── Free-text area with explicit save ────────────────────────────────────────

/**
 * Unlike the dropdowns, this one waits for an explicit Save — you don't want a
 * request firing mid-sentence while someone is still typing a paragraph.
 */
export function TextAreaField({ label, value, onSave, placeholder, maxLength = 500, helper }) {
  const [draft, setDraft] = useState(value || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => { setDraft(value || '') }, [value])

  const dirty = draft.trim() !== (value || '').trim()

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      await onSave(draft.trim())
      setSaved(true)
      setTimeout(() => setSaved(false), 2200)
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save that. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '15px 0', borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.mute, marginBottom: 8 }}>
        {label}
        <SavedFlash show={saved} />
      </div>

      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={4}
        aria-label={label}
        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.55, fontSize: 14 }}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 7, gap: 12 }}>
        <span style={{ fontSize: 12, color: JAI_COLORS.mute }}>
          {draft.length}/{maxLength}
        </span>
        {dirty && (
          <LinkButton onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </LinkButton>
        )}
      </div>

      {helper && (
        <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.55, color: JAI_COLORS.mute }}>
          {helper}
        </div>
      )}
      <FieldError>{error}</FieldError>
    </div>
  )
}

// ── Password input (used by the confirm-with-password flows) ─────────────────

export function PasswordField({ label, value, onChange, autoComplete = 'current-password', onEnter }) {
  return (
    <div style={{ marginTop: 12 }}>
      <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.mute, marginBottom: 6 }}>
        {label}
      </label>
      <input
        type="password"
        value={value}
        autoComplete={autoComplete}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter() } }}
        style={inputStyle}
      />
    </div>
  )
}
